import {
	MOTION_AT_REST,
	type MotionDirection,
	type MotionFrame,
	type MotionKindDefinition,
	type MotionRole,
	REDUCED_MOTION_MAX_MS,
} from "./define-motion-kind";
import { ANIMATION_KINDS } from "./kinds/animations";
import { TRANSITION_KINDS } from "./kinds/transitions";

export type TransitionKindKey = (typeof TRANSITION_KINDS)[number]["key"];
export type AnimationKindKey = (typeof ANIMATION_KINDS)[number]["key"];

function byKey<T extends readonly MotionKindDefinition[]>(kinds: T) {
	return Object.fromEntries(kinds.map((kind) => [kind.key, kind])) as Record<
		T[number]["key"],
		MotionKindDefinition
	>;
}

/** The motion registry (R10): every transition and animation kind, keyed by role then key. */
export const MOTION_KIND_REGISTRY = {
	transition: byKey(TRANSITION_KINDS),
	animation: byKey(ANIMATION_KINDS),
} as const satisfies Record<MotionRole, Record<string, MotionKindDefinition>>;

export const TRANSITION_KIND_KEYS = TRANSITION_KINDS.map(
	(kind) => kind.key,
) as unknown as readonly [TransitionKindKey, ...TransitionKindKey[]];
export const ANIMATION_KIND_KEYS = ANIMATION_KINDS.map(
	(kind) => kind.key,
) as unknown as readonly [AnimationKindKey, ...AnimationKindKey[]];

/** Kinds that take no time and render at rest: `cut` (transition), `none` (animation). */
export const STILL_KIND_KEY: Record<MotionRole, string> = {
	transition: "cut",
	animation: "none",
};

const KIND_KEYS_BY_ROLE: Record<MotionRole, ReadonlySet<string>> = {
	transition: new Set(Object.keys(MOTION_KIND_REGISTRY.transition)),
	animation: new Set(Object.keys(MOTION_KIND_REGISTRY.animation)),
};

export function isMotionKindKey(role: MotionRole, key: string): boolean {
	return KIND_KEYS_BY_ROLE[role].has(key);
}

/** Definition for a kind; throws on an unknown key (fail closed). */
export function getMotionKind(
	role: MotionRole,
	key: string,
): MotionKindDefinition {
	if (!isMotionKindKey(role, key)) {
		throw new Error(`Unknown ${role} motion kind: ${key}`);
	}
	return MOTION_KIND_REGISTRY[role][
		key as keyof (typeof MOTION_KIND_REGISTRY)[typeof role]
	];
}

export const MAX_MOTION_MS = 2000;

export interface MotionPlayback {
	role: MotionRole;
	kindKey: string;
	durationMs: number;
	params: Record<string, unknown>;
}

/**
 * What actually plays: params validated against the kind (invalid → the kind's defaults), duration
 * clamped to 0..2000 ms (0 for still kinds), and under reduced motion the kind's fallback
 * (opacity fade ≤150 ms, or still).
 */
export function resolveMotionPlayback(input: {
	role: MotionRole;
	kindKey: string;
	durationMs: number;
	params?: Record<string, unknown> | null;
	reducedMotion: boolean;
}): MotionPlayback {
	const kind = getMotionKind(input.role, input.kindKey);
	const parsed = kind.paramsSchema.safeParse(
		input.params ?? kind.defaultParams,
	);
	const params = parsed.success ? parsed.data : kind.defaultParams;
	const durationMs =
		kind.defaultDurationMs === 0
			? 0
			: Math.max(
					0,
					Math.min(MAX_MOTION_MS, Math.round(input.durationMs)),
				);
	if (!input.reducedMotion || durationMs === 0) {
		return { role: input.role, kindKey: kind.key, durationMs, params };
	}
	if (kind.reducedMotionFallback === "none") {
		return {
			role: input.role,
			kindKey: STILL_KIND_KEY[input.role],
			durationMs: 0,
			params: {},
		};
	}
	return {
		role: input.role,
		kindKey: "fade",
		durationMs: Math.min(durationMs, REDUCED_MOTION_MAX_MS),
		params: {},
	};
}

/** One frame of a playback; frames are relative to the start of the motion window. */
export function renderMotionFrame(input: {
	playback: Pick<MotionPlayback, "role" | "kindKey" | "params">;
	frame: number;
	durationInFrames: number;
	direction: MotionDirection;
}): MotionFrame {
	if (input.durationInFrames <= 0) {
		return MOTION_AT_REST;
	}
	const kind = getMotionKind(input.playback.role, input.playback.kindKey);
	return kind.render({
		frame: input.frame,
		durationInFrames: input.durationInFrames,
		direction: input.direction,
		params: input.playback.params,
	});
}
